import { initializeApp } from 'firebase/app';
import {
  getRemoteConfig,
  fetchAndActivate,
  getAll,
} from 'firebase/remote-config';
import { config as localConfig } from '@/config';

// Use the existing Firebase config
const firebaseConfig = localConfig.firebase;

// Create a Firebase app instance specifically for Remote Config
// This ensures we don't conflict with other Firebase instances
const remoteConfigApp = initializeApp(firebaseConfig, 'remoteConfig');

// Initialize Remote Config
const remoteConfig = getRemoteConfig(remoteConfigApp);

// Set default values (used during development or when fetch fails)
const defaultValues = {
  // API endpoints
  apiBaseUrl: localConfig.apiBaseUrl,
  callbackUrl: localConfig.callbackUrl,

  // Discogs OAuth URL configuration (not credentials)
  discogsRequestTokenUrl: 'https://api.discogs.com/oauth/request_token',
  discogsAuthUrl: 'https://www.discogs.com/oauth/authorize',
  discogsAccessTokenUrl: 'https://api.discogs.com/oauth/access_token',

  // Firebase configuration
  firebaseApiKey: localConfig.firebase.apiKey,
  firebaseAuthDomain: localConfig.firebase.authDomain,
  firebaseProjectId: localConfig.firebase.projectId,
  firebaseStorageBucket: localConfig.firebase.storageBucket,
  firebaseMessagingSenderId: localConfig.firebase.messagingSenderId,
  firebaseAppId: localConfig.firebase.appId,
  firebaseMeasurementId: localConfig.firebase.measurementId,

  // Application parameters
  tokenExpiryTime: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
};

// Set the defaults in Remote Config
remoteConfig.defaultConfig = defaultValues;

// In development, use shorter cache duration
if (process.env.NODE_ENV === 'development') {
  remoteConfig.settings.minimumFetchIntervalMillis = 0; // No cache in dev
} else {
  remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour in production
}

// Initialize config service
export class ConfigService {
  static isInitialized = false;

  static configValues = { ...defaultValues };

  static async initialize() {
    if (this.isInitialized) return this.configValues;

    try {
      // Fetch and activate remote configuration
      const activated = await fetchAndActivate(remoteConfig);
      console.log('==== Remote Config Status ====');
      console.log('Remote config activated:', activated);

      // Get all key-value pairs
      const allConfigs = getAll(remoteConfig);

      // List all available keys
      console.log('==== Available Remote Config Keys ====');
      console.log(Object.keys(allConfigs));

      // Update the config values; in development, local URLs win so the app talks to the emulator
      const localOnlyKeys = process.env.NODE_ENV === 'development' ? ['apiBaseUrl', 'callbackUrl'] : [];
      Object.keys(allConfigs)
        .filter(key => !localOnlyKeys.includes(key))
        .forEach(key => {
          this.configValues[key] = allConfigs[key].asString();
        });

      // Log non-sensitive values for debugging
      console.log('==== Remote Config Values ====');
      console.log('apiBaseUrl:', this.configValues.apiBaseUrl);
      console.log('callbackUrl:', this.configValues.callbackUrl);
      console.log('discogsRequestTokenUrl:', this.configValues.discogsRequestTokenUrl);
      console.log('discogsAuthUrl:', this.configValues.discogsAuthUrl);
      console.log('discogsAccessTokenUrl:', this.configValues.discogsAccessTokenUrl);
      console.log('tokenExpiryTime:', this.configValues.tokenExpiryTime);

      // Log Firebase project info (no secrets)
      console.log('==== Firebase Project Info ====');
      console.log('firebaseProjectId:', this.configValues.firebaseProjectId);
      console.log('firebaseAuthDomain:', this.configValues.firebaseAuthDomain);
      console.log('firebaseStorageBucket:', this.configValues.firebaseStorageBucket);

      this.isInitialized = true;
      console.log('==== Remote Config Initialization Complete ====');
    } catch (error) {
      console.error('==== Remote Config Error ====');
      console.error('Error fetching remote config:', error);
      console.log('Using default values:', this.configValues);
      // Continue with default values on error
    }

    return this.configValues;
  }

  static get(key) {
    if (!this.isInitialized) {
      console.warn('ConfigService accessed before initialization. Using default values.');
    }
    return this.configValues[key];
  }

  static getAll() {
    return { ...this.configValues };
  }
}

// Initialize on import
ConfigService.initialize().catch(error => {
  console.error('Failed to initialize config service:', error);
});

export default ConfigService;
